import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
}

/** Fenêtres ouvertes, la plus récente en dernier : seule celle du dessus réagit à Échap. */
const openStack: symbol[] = [];

const sizes = {
  sm: "sm:max-w-md",
  md: "sm:max-w-xl",
  lg: "sm:max-w-3xl",
  xl: "sm:max-w-5xl",
};

/**
 * Fenêtre : centrée sur ordinateur (elle apparaît en s'agrandissant légèrement), en feuille qui monte du
 * bas sur téléphone, au-dessus de la barre d'accueil. Échap ferme la fenêtre du dessus (une fenêtre peut
 * en ouvrir une autre), le focus va dans la fenêtre puis revient où il était.
 */
export default function Modal({ open, onClose, title, subtitle, icon, children, footer, size = "md" }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const id = Symbol("modal");
    openStack.push(id);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && openStack[openStack.length - 1] === id) onCloseRef.current();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    panelRef.current?.focus({ preventScroll: true });
    return () => {
      document.removeEventListener("keydown", onKey);
      openStack.splice(openStack.indexOf(id), 1);
      if (!openStack.length) document.body.style.overflow = "";
      previous?.focus?.({ preventScroll: true });
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center sm:p-6 md:p-8">
      <div className="fixed inset-0 bg-foreground-950/45 animate-fade-in" onClick={onClose} aria-hidden="true"></div>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={`relative z-10 flex max-h-[92dvh] w-full flex-col rounded-t-xl border border-background-200 bg-background-50 shadow-modal outline-none animate-sheet-up sm:max-h-[88dvh] sm:rounded-xl sm:animate-scale-in ${sizes[size]}`}
      >
        {/* Poignée : rappelle sur téléphone qu'il s'agit d'une feuille. */}
        <span className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-background-300 sm:hidden" aria-hidden="true"></span>
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-background-200 px-4 py-3.5 sm:px-6 sm:py-4">
          <div className="flex min-w-0 items-start gap-3">
            {icon && (
              <span className="w-9 h-9 rounded-md bg-primary-50 text-primary-700 flex items-center justify-center shrink-0" aria-hidden="true">
                <i className={`${icon} text-lg`}></i>
              </span>
            )}
            <div className="min-w-0">
              <h3 className="text-base font-semibold font-heading text-foreground-950 break-words">{title}</h3>
              {subtitle && <p className="text-sm text-foreground-500 mt-0.5 break-words">{subtitle}</p>}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="press -mr-1 w-10 h-10 sm:w-8 sm:h-8 shrink-0 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 hover:text-foreground-800 cursor-pointer"
            aria-label="Fermer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 sm:py-5">{children}</div>
        {footer && (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-background-200 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] sm:gap-3 sm:px-6 sm:py-4 [&>*]:flex-1 sm:[&>*]:flex-none">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
